/**
 * Active Directory — connect on-prem AD over LDAP/LDAPS, sync users and groups,
 * and see what the sync brought in.
 *
 * Synced groups are what LLM Access Policy and Agent Policies target with an
 * "AD group" rule. Membership is resolved transitively (nested groups count),
 * and a disabled AD account stops matching group allows and fails the
 * endpoint's identity lease — offboarding in AD reaches AI access.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Icon } from '../components/ui';
import { usePermission } from '../hooks/usePermission';
import * as api from '../lib/directoryApi';
import type { DirectoryConfig, DirectoryConfigInput, DirectoryGroup, DirectoryUser } from '../lib/directoryApi';
import { Trans, useTranslation } from 'react-i18next';
import { parseServerDate } from '../i18n/format';

interface Props {
  apiUrl: string;
}

const EMPTY: DirectoryConfigInput = {
  url: 'ldaps://',
  bindDn: '',
  bindPassword: '',
  baseDn: '',
  userFilter: '',
  groupFilter: '',
  netbiosDomain: '',
  caPem: '',
  tlsVerify: true,
  startTls: false,
  syncIntervalMin: 15,
  enabled: true,
};

const toInput = (c: DirectoryConfig): DirectoryConfigInput => ({
  url: c.url,
  bindDn: c.bindDn,
  bindPassword: '',
  baseDn: c.baseDn,
  userFilter: c.userFilter || '',
  groupFilter: c.groupFilter || '',
  netbiosDomain: c.netbiosDomain || '',
  caPem: c.caPem || '',
  tlsVerify: c.tlsVerify,
  startTls: c.startTls,
  syncIntervalMin: c.syncIntervalMin,
  enabled: c.enabled,
});

const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode; wide?: boolean }> = ({
  label,
  hint,
  children,
  wide,
}) => (
  <label style={{ display: 'grid', gap: 4, fontSize: 12, gridColumn: wide ? '1 / -1' : undefined }}>
    <span style={{ color: 'var(--text-2)' }}>{label}</span>
    {children}
    {hint && <span style={{ fontSize: 10, color: 'var(--text-2)' }}>{hint}</span>}
  </label>
);

const DirectorySettings: React.FC<Props> = ({ apiUrl }) => {
  const { t } = useTranslation();
  const { t: tr } = useTranslation();
  const canWrite = usePermission('manage:settings');

  const [config, setConfig] = useState<DirectoryConfig | null>(null);
  const [form, setForm] = useState<DirectoryConfigInput>(EMPTY);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<{ message: string; hint?: string } | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const [groups, setGroups] = useState<DirectoryGroup[]>([]);
  const [groupQ, setGroupQ] = useState('');
  const [openGroup, setOpenGroup] = useState<DirectoryGroup | null>(null);
  const [members, setMembers] = useState<DirectoryUser[]>([]);
  const [users, setUsers] = useState<DirectoryUser[]>([]);
  const [userQ, setUserQ] = useState('');

  const loadConfig = useCallback(async () => {
    try {
      const c = await api.getConfig(apiUrl);
      setConfig(c);
      if (c) setForm(toInput(c));
    } catch (e: any) {
      setError({ message: e.message });
    }
  }, [apiUrl]);

  const loadDirectory = useCallback(async () => {
    try {
      const [g, u] = await Promise.all([api.listGroups(apiUrl, groupQ, 200), api.listUsers(apiUrl, userQ, 200)]);
      setGroups(g);
      setUsers(u);
    } catch {
      /* nothing synced yet */
    }
  }, [apiUrl, groupQ, userQ]);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);
  useEffect(() => {
    const t = setTimeout(() => void loadDirectory(), 250);
    return () => clearTimeout(t);
  }, [loadDirectory]);

  useEffect(() => {
    if (!openGroup) return;
    api.groupMembers(apiUrl, openGroup.sid).then(setMembers).catch(() => setMembers([]));
  }, [apiUrl, openGroup]);

  const set = <K extends keyof DirectoryConfigInput>(k: K, v: DirectoryConfigInput[K]) => setForm((f) => ({ ...f, [k]: v }));

  const run = async (key: string, fn: () => Promise<string>) => {
    setBusy(key);
    setError(null);
    setNote(null);
    try {
      setNote(await fn());
    } catch (e: any) {
      setError({ message: e.message, hint: e.hint });
    } finally {
      setBusy(null);
    }
  };

  const save = () =>
    run('save', async () => {
      const c = await api.saveConfig(apiUrl, form);
      setConfig(c);
      setForm(toInput(c));
      return 'Directory settings saved.';
    });

  const test = () =>
    run('test', async () => {
      const r = await api.testConnection(apiUrl, form);
      if (r.netbiosDomain && !form.netbiosDomain) set('netbiosDomain', r.netbiosDomain);
      return `Connected. Domain ${r.netbiosDomain || '(unknown)'} — found users and groups to sync.`;
    });

  const sync = () =>
    run('sync', async () => {
      const r = await api.syncNow(apiUrl);
      setConfig(r.config);
      await loadDirectory();
      return `Synced ${r.result.users} users, ${r.result.groups} groups, ${r.result.memberships} memberships in ${(r.result.durationMs / 1000).toFixed(1)}s.`;
    });

  const insecure = form.url.toLowerCase().startsWith('ldap://') && !form.startTls;
  const syncOk = config?.lastSyncStatus === 'ok';

  return (
    <div className="page-content">
      <div className="page-title">
        <div>
          <h1>{tr('pages.directorySettings.activeDirectory')}</h1>
          <p className="subtitle">
            {tr('pages.directorySettings.connectYourOnPremActiveDirectory')}
          </p>
        </div>
      </div>

      {error && (
        <div className="notice error">
          <Icon name="warning" /> {error.message}
          {error.hint && <div style={{ fontSize: 12, marginTop: 4 }}>{error.hint}</div>}
        </div>
      )}
      {note && (
        <div className="notice">
          <Icon name="checkCircle" /> {note}
        </div>
      )}

      {/* Status */}
      <div className="ent-card" style={{ marginBottom: 16 }}>
        <div className="ent-card-head">
          <Icon name="server" /> {tr('pages.directorySettings.status')}
        </div>
        <div className="ent-card-body" style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
          {!config ? (
            <span style={{ color: 'var(--text-2)' }}>{tr('pages.directorySettings.notConnectedFillInTheConnection')}</span>
          ) : (
            <>
              <div>
                <span className={`badge ${syncOk ? 'badge-success' : config.lastSyncStatus ? 'badge-error' : 'badge-unknown'}`}>
                  {syncOk ? tr('pages.directorySettings.inSync') : config.lastSyncStatus ? tr('pages.directorySettings.lastSyncFailed') : tr('pages.directorySettings.notSyncedYet')}
                </span>
                {!config.enabled && <span className="badge badge-unknown" style={{ marginLeft: 6 }}>{tr('pages.directorySettings.syncPaused')}</span>}
              </div>
              {config.lastSyncCounts && (
                <div style={{ fontSize: 12 }}>
                  <Trans
                    i18nKey="pages.directorySettings.syncCounts"
                    values={{ users: config.lastSyncCounts.users, groups: config.lastSyncCounts.groups, memberships: config.lastSyncCounts.memberships }}
                    components={{ s: <strong /> }}
                  />
                </div>
              )}
              {config.lastSyncAt && (
                <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{tr('pages.directorySettings.lastSyncEveryMin', { v0: parseServerDate(config.lastSyncAt).toLocaleString(), v1: config.syncIntervalMin })}</div>
              )}
              {config.lastSyncStatus && !syncOk && (
                <div style={{ fontSize: 12, color: 'var(--danger, #c0392b)', flexBasis: '100%' }}>{config.lastSyncStatus}</div>
              )}
              {canWrite && (
                <button className="ent-btn sm" style={{ marginLeft: 'auto' }} disabled={!!busy} onClick={() => void sync()}>
                  <Icon name="refresh" /> {busy === 'sync' ? tr('pages.directorySettings.syncing') : tr('pages.directorySettings.syncNow')}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Connection */}
      <div className="ent-card" style={{ marginBottom: 16 }}>
        <div className="ent-card-head">
          <Icon name="key" /> {tr('pages.directorySettings.connection')}
        </div>
        <div className="ent-card-body">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
            <Field label={t('pages.directorySettings.serverURL')} hint="ldaps://dc1.corp.example:636 — LDAPS recommended">
              <input className="ent-input" value={form.url} disabled={!canWrite} onChange={(e) => set('url', e.target.value)} />
            </Field>
            <Field label={t('pages.directorySettings.baseDN')} hint="Where users and groups are read from, e.g. DC=corp,DC=example">
              <input className="ent-input" value={form.baseDn} disabled={!canWrite} placeholder={tr('pages.directorySettings.dcCorpDCExample')} onChange={(e) => set('baseDn', e.target.value)} />
            </Field>
            <Field label={t('pages.directorySettings.bindAccount')} hint="A read-only service account: DN or user@domain">
              <input className="ent-input" value={form.bindDn} disabled={!canWrite} placeholder={tr('pages.directorySettings.svcAgenti5CorpExample')} onChange={(e) => set('bindDn', e.target.value)} />
            </Field>
            <Field label={t('pages.directorySettings.bindPassword')} hint={config?.hasPassword ? 'Stored encrypted. Leave blank to keep it.' : 'Stored encrypted.'}>
              <input
                className="ent-input"
                type="password"
                autoComplete="new-password"
                value={form.bindPassword || ''}
                disabled={!canWrite}
                placeholder={config?.hasPassword ? '••••••••' : ''}
                onChange={(e) => set('bindPassword', e.target.value)}
              />
            </Field>
          </div>

          {insecure && (
            <div className="notice error" style={{ marginTop: 12 }}>
              <Icon name="warning" /> {tr('pages.directorySettings.plainLdapSendsTheBindPassword')}
            </div>
          )}

          <button className="ent-btn sm" style={{ marginTop: 12 }} onClick={() => setShowAdvanced((v) => !v)}>
            <Icon name={showAdvanced ? 'chevronDown' : 'chevronRight'} /> {tr('pages.directorySettings.advanced')}
          </button>
          {showAdvanced && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, marginTop: 12 }}>
              <Field label={t('pages.directorySettings.netbiosDomain')} hint="How Windows names users (CORP\alice). Discovered automatically when blank.">
                <input className="ent-input" value={form.netbiosDomain || ''} disabled={!canWrite} placeholder={tr('pages.directorySettings.corp')} onChange={(e) => set('netbiosDomain', e.target.value)} />
              </Field>
              <Field label={t('pages.directorySettings.syncEveryMinutes')} hint="5 – 1440">
                <input
                  className="ent-input"
                  type="number"
                  min={5}
                  max={1440}
                  value={form.syncIntervalMin ?? 15}
                  disabled={!canWrite}
                  onChange={(e) => set('syncIntervalMin', Number(e.target.value))}
                />
              </Field>
              <Field label={t('pages.directorySettings.userFilter')} hint="Default: (&(objectCategory=person)(objectClass=user))">
                <input className="ent-input" value={form.userFilter || ''} disabled={!canWrite} onChange={(e) => set('userFilter', e.target.value)} />
              </Field>
              <Field label={t('pages.directorySettings.groupFilter')} hint="Default: (objectClass=group)">
                <input className="ent-input" value={form.groupFilter || ''} disabled={!canWrite} onChange={(e) => set('groupFilter', e.target.value)} />
              </Field>
              <Field label={t('pages.directorySettings.trustedCAPEM')} hint="The CA that issued the domain controller's certificate, if it isn't publicly trusted." wide>
                <textarea
                  className="ent-input"
                  rows={4}
                  style={{ fontFamily: 'monospace', fontSize: 11 }}
                  value={form.caPem || ''}
                  disabled={!canWrite}
                  placeholder={tr('pages.directorySettings.beginCERTIFICATE')}
                  onChange={(e) => set('caPem', e.target.value)}
                />
              </Field>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}>
                <input type="checkbox" checked={!!form.startTls} disabled={!canWrite} onChange={(e) => set('startTls', e.target.checked)} />
                {tr('pages.directorySettings.useStartTLSWithLdap')}
              </label>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}>
                <input type="checkbox" checked={form.tlsVerify !== false} disabled={!canWrite} onChange={(e) => set('tlsVerify', e.target.checked)} />
                {tr('pages.directorySettings.verifyTheServerCertificateTurnOff')}
              </label>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}>
                <input type="checkbox" checked={form.enabled !== false} disabled={!canWrite} onChange={(e) => set('enabled', e.target.checked)} />
                {tr('pages.directorySettings.syncOnASchedule')}
              </label>
            </div>
          )}

          {canWrite && (
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button className="ent-btn" disabled={!!busy} onClick={() => void test()}>
                <Icon name="signal" /> {busy === 'test' ? tr('pages.directorySettings.testing') : tr('pages.directorySettings.testConnection')}
              </button>
              <button className="ent-btn primary" disabled={!!busy} onClick={() => void save()}>
                <Icon name="check" /> {busy === 'save' ? tr('pages.directorySettings.saving') : tr('pages.directorySettings.save')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Groups */}
      <div className="ent-card" style={{ marginBottom: 16 }}>
        <div className="ent-card-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>
            <Icon name="server" /> {tr('pages.directorySettings.groups')}
          </span>
          <input className="ent-input" style={{ maxWidth: 220, fontSize: 12 }} placeholder={tr('pages.directorySettings.searchGroups')} value={groupQ} onChange={(e) => setGroupQ(e.target.value)} />
        </div>
        <div className="ent-card-body">
          {groups.length === 0 ? (
            <div className="ent-empty">{syncOk ? tr('pages.directorySettings.noGroupsMatch') : tr('pages.directorySettings.groupsAppearHereAfterTheFirst')}</div>
          ) : (
            <div className="ent-table-wrap scroll" style={{ maxHeight: 360 }}>
              <table className="ent-table">
                <thead>
                  <tr>
                    <th>{tr('pages.directorySettings.group')}</th>
                    <th>{tr('pages.directorySettings.members')}</th>
                    <th>{tr('pages.directorySettings.sid')}</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((g) => (
                    <tr key={g.sid} style={{ cursor: 'pointer' }} onClick={() => setOpenGroup(openGroup?.sid === g.sid ? null : g)}>
                      <td>
                        <strong>{g.name}</strong>
                        {g.description && <div style={{ fontSize: 10, color: 'var(--text-2)' }}>{g.description}</div>}
                      </td>
                      <td style={{ fontSize: 12 }}>{g.memberCount ?? '—'}</td>
                      <td style={{ fontSize: 10, fontFamily: 'monospace', color: 'var(--text-2)' }}>{g.sid}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {openGroup && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12, marginBottom: 6 }}>
                {tr('pages.directorySettings.membersOf')} <strong>{openGroup.name}</strong> {tr('pages.directorySettings.includingNestedGroups')}
              </div>
              {members.length === 0 ? (
                <div className="ent-empty">{tr('pages.directorySettings.noMembers')}</div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {members.map((m) => (
                    <span key={m.sid} className={`badge ${m.disabled ? 'badge-unknown' : 'badge-info'}`} title={m.upn || m.sam || m.sid}>
                      {m.displayName || m.sam}
                      {m.disabled ? t('pages.directorySettings.disabledSuffix') : ''}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Users */}
      <div className="ent-card">
        <div className="ent-card-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>
            <Icon name="users" /> {tr('pages.directorySettings.users')}
          </span>
          <input className="ent-input" style={{ maxWidth: 220, fontSize: 12 }} placeholder={tr('pages.directorySettings.searchUsers')} value={userQ} onChange={(e) => setUserQ(e.target.value)} />
        </div>
        <div className="ent-card-body">
          {users.length === 0 ? (
            <div className="ent-empty">{syncOk ? tr('pages.directorySettings.noUsersMatch') : tr('pages.directorySettings.usersAppearHereAfterTheFirst')}</div>
          ) : (
            <div className="ent-table-wrap scroll" style={{ maxHeight: 360 }}>
              <table className="ent-table">
                <thead>
                  <tr>
                    <th>{tr('pages.directorySettings.name')}</th>
                    <th>{tr('pages.directorySettings.signIn')}</th>
                    <th>{tr('pages.directorySettings.windows')}</th>
                    <th>{tr('pages.directorySettings.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.sid}>
                      <td>
                        <strong>{u.displayName || u.sam}</strong>
                        {u.mail && <div style={{ fontSize: 10, color: 'var(--text-2)' }}>{u.mail}</div>}
                      </td>
                      <td style={{ fontSize: 12 }}>{u.upn || '—'}</td>
                      <td style={{ fontSize: 12, fontFamily: 'monospace' }}>
                        {u.netbiosDomain ? `${u.netbiosDomain}\\${u.sam}` : u.sam}
                      </td>
                      <td>
                        <span className={`badge ${u.disabled ? 'badge-error' : 'badge-success'}`}>{u.disabled ? tr('pages.directorySettings.disabled') : tr('pages.directorySettings.active')}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DirectorySettings;
